import React, { useMemo, useRef, useState } from "react";
import { FaLongArrowAltLeft } from "react-icons/fa";
import { RiPencilFill } from "react-icons/ri";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { commonToast } from "../helper/Toast";
import { useTranslation } from "react-i18next";
import { ApiPost, ApiPut } from "../helper/axiosFunctions";
import Loading from "../components/Loading";
import { setProfileData } from "../redux/slices/profileSlice";
import UserAvatar, { isRealProfilePicture } from "../components/UserAvatar";

const ACCEPT_IMAGES = "image/jpeg,image/png,image/webp,image/gif";

const getStoredUserBundle = () => {
  try {
    return JSON.parse(localStorage.getItem("zubrus_user_data")) || null;
  } catch {
    return null;
  }
};

const getRoleData = (userInfo, roleKey) =>
  userInfo?.roleData?.[roleKey] || {};

/**
 * Pick the profile update endpoint + body based on the user's primary role.
 * Priority: content creator → teacher → parent → student → generic /users.
 */
const buildRoleProfileUpdate = (userInfo, { firstName, lastName, profilePictureUrl }) => {
  const roles = userInfo?.userRoles || [];
  const preferences = userInfo?.userPreferences;
  const metadata = userInfo?.metadata;

  const shared = {
    firstName,
    lastName,
    email: userInfo?.email,
    preferredLanguage: userInfo?.preferredLanguage,
    countryCode: userInfo?.countryCode,
    profilePictureUrl:
      profilePictureUrl ?? userInfo?.profilePictureUrl ?? undefined,
  };

  if (preferences) shared.userPreferences = preferences;
  if (metadata) shared.metadata = metadata;

  if (roles.includes("zubrus_content_creator")) {
    const rd = getRoleData(userInfo, "zubrus_content_creator");
    return {
      endpoint: "/users/content-creators",
      body: {
        contentCreatorId:
          rd.contentCreatorId ||
          userInfo?.contentCreatorId ||
          userInfo?.userId,
        username: userInfo?.username,
        ...shared,
      },
    };
  }

  if (roles.includes("zubrus_teacher")) {
    const rd = getRoleData(userInfo, "zubrus_teacher");
    return {
      endpoint: "/users/teachers",
      body: {
        teacherId: rd.teacherId || userInfo?.teacherId || userInfo?.userId,
        ...shared,
        schoolIds: rd.schoolIds || userInfo?.schoolIds || [],
        subjectsToTeach: rd.subjectsToTeach || userInfo?.subjectsToTeach || [],
      },
    };
  }

  if (roles.includes("zubrus_parent")) {
    const rd = getRoleData(userInfo, "zubrus_parent");
    return {
      endpoint: "/users/parents",
      body: {
        parentId: rd.parentId || userInfo?.parentId || userInfo?.userId,
        username: userInfo?.username,
        ...shared,
        schoolChildList: rd.schoolChildList || userInfo?.schoolChildList || [],
      },
    };
  }

  if (roles.includes("zubrus_student")) {
    const rd = getRoleData(userInfo, "zubrus_student");
    return {
      endpoint: "/users/students",
      body: {
        studentId: rd.studentId || userInfo?.studentId || userInfo?.userId,
        ...shared,
        schoolId: rd.schoolId || userInfo?.schoolId,
        gender: rd.gender || userInfo?.gender,
        birthDate: rd.birthDate || userInfo?.birthDate,
        nickname: rd.nickname || userInfo?.nickname,
        avatarName: rd.avatarName || userInfo?.avatarName,
        avatarBackgroundColor:
          rd.avatarBackgroundColor || userInfo?.avatarBackgroundColor,
        avatarUrl: rd.avatarUrl || userInfo?.avatarUrl,
      },
    };
  }

  // Fallback for admins / unknown roles
  return {
    endpoint: "/users",
    body: {
      userId: userInfo?.userId,
      username: userInfo?.username,
      firstName,
      lastName,
      profilePictureUrl: shared.profilePictureUrl,
    },
  };
};

const uploadProfilePicture = async (file, userId) => {
  // Same common upload API used for deck images elsewhere.
  // `deckId` is the storage folder key — use the userId for profile pics.
  const formData = new FormData();
  formData.append("deckId", userId || "profile");
  formData.append("file1", file);

  const response = await ApiPost("/contents/images-upload/deck", formData);
  const uploadedUrls = response?.data || [];
  const url = Array.isArray(uploadedUrls) ? uploadedUrls[0] : uploadedUrls;
  if (url && typeof url === "string") return url;
  throw new Error("No URL returned from upload");
};

export default function UserInfo() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  const storedBundle = useMemo(() => getStoredUserBundle(), []);
  const userData = storedBundle?.data;
  const userInfo = userData?.userInfo;

  const [firstName, setFirstName] = useState(userInfo?.firstName || "");
  const [lastName, setLastName] = useState(userInfo?.lastName || "");
  const [profilePictureUrl, setProfilePictureUrl] = useState(
    isRealProfilePicture(userInfo?.profilePictureUrl)
      ? userInfo.profilePictureUrl
      : ""
  );
  const [previewUrl, setPreviewUrl] = useState(
    isRealProfilePicture(userInfo?.profilePictureUrl)
      ? userInfo.profilePictureUrl
      : ""
  );
  const [pendingFile, setPendingFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const validateForm = () => {
    const validationErrors = {};
    if (!firstName?.trim()) validationErrors.firstName = t("nameRequired");
    if (!lastName?.trim()) validationErrors.lastName = t("lastNameRequired");
    return validationErrors;
  };

  const persistUserInfo = (nextUserInfo) => {
    const bundle = getStoredUserBundle() || { data: userData };
    const next = {
      ...bundle,
      data: {
        ...(bundle.data || userData),
        userInfo: {
          ...(bundle.data?.userInfo || userInfo),
          ...nextUserInfo,
        },
      },
    };
    localStorage.setItem("zubrus_user_data", JSON.stringify(next));
  };

  const handlePickImage = () => {
    if (loading || uploading) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!file.type?.startsWith("image/")) {
      commonToast(t("invalidImageType") || "Please select an image file", "error");
      return;
    }

    // ~5MB guard
    if (file.size > 5 * 1024 * 1024) {
      commonToast(t("imageTooLarge") || "Image must be under 5MB", "error");
      return;
    }

    if (previewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(previewUrl);
    }

    const localPreview = URL.createObjectURL(file);
    setPendingFile(file);
    setPreviewUrl(localPreview);
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setLoading(true);

    try {
      let nextPictureUrl = profilePictureUrl;

      if (pendingFile) {
        setUploading(true);
        nextPictureUrl = await uploadProfilePicture(
          pendingFile,
          userInfo?.userId
        );
        setUploading(false);
        setProfilePictureUrl(nextPictureUrl);
        if (previewUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl(nextPictureUrl);
        setPendingFile(null);
      }

      const latestBundle = getStoredUserBundle();
      const latestUserInfo = latestBundle?.data?.userInfo || userInfo;
      const { endpoint, body } = buildRoleProfileUpdate(latestUserInfo, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        profilePictureUrl: nextPictureUrl || undefined,
      });

      const response = await ApiPut(endpoint, body);

      if (response?.code === 200 || response?.data || response) {
        const updatedUserInfo = {
          ...latestUserInfo,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          profilePictureUrl: nextPictureUrl || latestUserInfo?.profilePictureUrl,
        };
        commonToast(t("personalDataSaved") || t("Personaldata"), "success");
        persistUserInfo(updatedUserInfo);
        dispatch(setProfileData(updatedUserInfo));
      }
    } catch (error) {
      commonToast(
        error?.data?.errorDescription ||
          error?.data?.error ||
          error?.message ||
          t("updateFailed") ||
          "Update failed",
        "error"
      );
      console.error("Error updating user info:", error);
    } finally {
      setUploading(false);
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto py-10 md:px-4">
      <div className="flex items-center md:gap-2">
        <Link
          to="/profile"
          className="border cursor-pointer rounded-full flex justify-center items-center size-10"
        >
          <FaLongArrowAltLeft />
        </Link>
        <h3 className="text-lg font-bold flex-1 text-center">
          {t("personaldata")}
        </h3>
      </div>

      <div className="my-10 relative mx-auto w-fit">
        <button
          type="button"
          onClick={handlePickImage}
          className="relative block rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-darkblue"
          aria-label={t("uploadImage") || "Upload profile picture"}
          disabled={loading || uploading}
        >
          <UserAvatar
            src={previewUrl}
            firstName={firstName}
            lastName={lastName}
            username={userInfo?.username}
            email={userInfo?.email}
            size="lg"
            alt="User"
          />
          {(loading || uploading) && (
            <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40">
              <Loading />
            </div>
          )}
          <span className="absolute bottom-3 right-1 shadow-md bg-white rounded-full text-4xl p-2 pointer-events-none">
            <RiPencilFill />
          </span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT_IMAGES}
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      <div className="relative">
        <div className="mb-1 absolute z-10 mt-1.5 flex justify-between w-full px-4 text-sm text-gray-500">
          <label>{t("name")}</label>
        </div>
        <input
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder={t("name")}
          className={`border h-[56px] w-full z-20 relative rounded-xl bg-transparent px-4 py-2 shadow-inner outline-none placeholder:text-gray-500 text-black tracking-widest pt-5 ${
            errors.firstName ? "border-red-500" : ""
          }`}
        />
        {errors.firstName && (
          <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
        )}
      </div>

      <div className="relative mt-4">
        <div className="mb-1 absolute z-10 mt-1.5 flex justify-between w-full px-4 text-sm text-gray-500">
          <label>{t("lastname")}</label>
        </div>
        <input
          type="text"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder={t("lastname")}
          className={`border h-[56px] w-full z-20 relative rounded-xl bg-transparent px-4 py-2 shadow-inner outline-none placeholder:text-gray-500 text-black tracking-widest pt-5 ${
            errors.lastName ? "border-red-500" : ""
          }`}
        />
        {errors.lastName && (
          <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
        )}
      </div>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={loading || uploading}
        className="primary-btn w-full mt-5 disabled:opacity-60"
      >
        {loading || uploading ? <Loading /> : t("save")}
      </button>
    </div>
  );
}
